import React, { useEffect, useMemo } from "react";

export const NotFound = () => {
  const bgColor = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const bg = params.get('backgroundColor');
    return bg ? `#${bg}` : '#f2f3f7';
  }, []);

  useEffect(() => {
    const originalBackground = document.body.style.backgroundColor;
    document.body.style.backgroundColor = bgColor;
    return () => {
      document.body.style.backgroundColor = originalBackground;
    };
  }, [bgColor]);

  return (
    <div style={{ ...styles.wrapper, backgroundColor: bgColor }}>
      <div style={styles.phone}>
        <div style={styles.notch} />
        <div style={styles.screen}>
          {/* Logo */}
          <div style={styles.logoContainer}>
            <img alt="Veritran" width={180} src="/img/veritran_logo.svg" />
          </div>

          {/* Hourglass animation */}
          <div style={styles.hourglassContainer}>
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={styles.hourglass}>
              <path d="M16 8h32v12c0 6-6 10-12 14-2 1.3-4 2.7-4 4s2 2.7 4 4c6 4 12 8 12 14v12H16V56c0-6 6-10 12-14 2-1.3 4-2.7 4-4s-2-2.7-4-4C22 30 16 26 16 20V8z" stroke="#01087A" strokeWidth="3" fill="none"/>
              <rect x="14" y="5" width="36" height="4" rx="2" fill="#01087A"/>
              <rect x="14" y="59" width="36" height="4" rx="2" fill="#01087A"/>
              {/* Sand top */}
              <path d="M24 16h16v4c0 2-3 4-6 6l-2 1-2-1c-3-2-6-4-6-6v-4z" fill="#0066FF" opacity="0.3">
                <animate attributeName="opacity" values="0.3;0.1;0.3" dur="2.5s" repeatCount="indefinite"/>
              </path>
              {/* Sand stream */}
              <rect x="31" y="28" width="2" height="8" rx="1" fill="#0066FF" opacity="0.5">
                <animate attributeName="height" values="8;4;8" dur="2.5s" repeatCount="indefinite"/>
                <animate attributeName="y" values="28;30;28" dur="2.5s" repeatCount="indefinite"/>
              </rect>
              {/* Sand bottom */}
              <path d="M24 52h16v-4c0-2-3-4-6-6l-2-1-2 1c-3 2-6 4-6 6v4z" fill="#0066FF" opacity="0.2">
                <animate attributeName="opacity" values="0.2;0.5;0.2" dur="2.5s" repeatCount="indefinite"/>
              </path>
            </svg>
          </div>

          {/* Message */}
          <div style={styles.title}>Preparing your preview</div>
          <div style={styles.subtitle}>
            We're collecting the information needed to generate your experience.
          </div>

          {/* Loading dots */}
          <div style={styles.dotsContainer}>
            <span style={{ ...styles.dot, animationDelay: '0ms' }} />
            <span style={{ ...styles.dot, animationDelay: '200ms' }} />
            <span style={{ ...styles.dot, animationDelay: '400ms' }} />
          </div>

          <div style={styles.footer}>
            Powered by <span style={styles.bold}>Veritran</span>
          </div>
        </div>
        <div style={styles.homeBar} />
      </div>

      <style>{`
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes hourglassRotate {
          0% { transform: rotate(0deg); }
          40% { transform: rotate(0deg); }
          50% { transform: rotate(180deg); }
          90% { transform: rotate(180deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const styles = {
  wrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f2f3f7',
    fontFamily: '"Inter", Helvetica, sans-serif',
  },
  phone: {
    width: 450,
    height: 900,
    backgroundColor: '#ffffff',
    borderRadius: 50,
    border: '15px solid #000000',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 30,
  },
  notch: {
    width: 150,
    height: 30,
    backgroundColor: '#000000',
    borderRadius: '0 0 20px 20px',
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10,
  },
  screen: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 32px 40px',
    textAlign: 'center',
  },
  logoContainer: {
    marginBottom: 48,
  },
  hourglassContainer: {
    marginBottom: 32,
  },
  hourglass: {
    animation: 'hourglassRotate 4s ease-in-out infinite',
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: '#01087A',
    marginBottom: 12,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: 15,
    fontWeight: 400,
    color: '#6B7280',
    lineHeight: 1.6,
    maxWidth: 300,
    marginBottom: 32,
  },
  dotsContainer: {
    display: 'flex',
    gap: 8,
    marginBottom: 48,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#0066FF',
    display: 'inline-block',
    animation: 'dotPulse 1.4s ease-in-out infinite',
  },
  footer: {
    position: 'absolute',
    bottom: 40,
    fontSize: 12,
    color: '#9CA3AF',
  },
  bold: {
    fontWeight: 700,
    color: '#01087A',
  },
  homeBar: {
    width: 134,
    height: 5,
    backgroundColor: '#1a1a2e',
    borderRadius: 3,
    position: 'absolute',
    bottom: 10,
    left: '50%',
    transform: 'translateX(-50%)',
  },
};
